import { ArrowLeft, Clock, Tag } from 'lucide-react'
import type { BlogPost as BlogPostType } from '../lib/types'

interface BlogPostProps {
  post: BlogPostType
  onBack: () => void
}

export function BlogPost({ post, onBack }: BlogPostProps) {
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl px-4 py-8 lg:px-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-small text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg))] transition-colors mb-6 group"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          Back to Articles
        </button>

        <article>
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 text-caption text-[hsl(var(--fg-muted))] mb-3">
              <span>{post.date}</span>
              <span className="w-1 h-1 rounded-full bg-[hsl(var(--fg-dim))]" />
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {post.readingTime} min read
              </span>
              <span className="w-1 h-1 rounded-full bg-[hsl(var(--fg-dim))]" />
              <span>{post.author}</span>
            </div>

            <h1 className="text-h1 text-[hsl(var(--fg))] font-bold leading-tight mb-4">
              {post.title}
            </h1>

            <div className="flex flex-wrap gap-2">
              {post.tags.map(tag => (
                <span
                  key={tag}
                  className="flex items-center gap-1 rounded-md bg-[hsl(var(--accent-soft))] px-2.5 py-1 text-caption text-[hsl(var(--accent))]"
                >
                  <Tag className="h-3 w-3" />
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div className="prose prose-invert prose-sm max-w-none">
            {post.content.split('\n').map((line, i) => {
              if (line.startsWith('## ')) {
                return <h2 key={i} className="text-h3 text-[hsl(var(--fg))] font-semibold mt-8 mb-3">{line.replace('## ', '')}</h2>
              }
              if (line.startsWith('### ')) {
                return <h3 key={i} className="text-h4 text-[hsl(var(--fg))] font-semibold mt-6 mb-2">{line.replace('### ', '')}</h3>
              }
              if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={i} className="text-body font-semibold text-[hsl(var(--fg))] mt-4 mb-2">{line.replace(/\*\*/g, '')}</p>
              }
              if (line.startsWith('> ')) {
                return (
                  <blockquote key={i} className="border-l-2 border-[hsl(var(--accent))] pl-4 py-1 my-4 text-body text-[hsl(var(--fg-muted))] italic">
                    {line.replace('> ', '')}
                  </blockquote>
                )
              }
              if (line.startsWith('| ') && line.includes(' | ')) {
                return null
              }
              if (line.startsWith('---')) {
                return <hr key={i} className="my-6 border-[hsl(var(--border))]" />
              }
              if (line.trim() === '') {
                return <div key={i} className="h-3" />
              }
              return (
                <p key={i} className="text-body text-[hsl(var(--fg))] leading-relaxed mb-3">
                  {line}
                </p>
              )
            })}
          </div>
        </article>
      </div>
    </div>
  )
}
